import { Rubles } from 'Cloud/Application/Rubles';
import config from 'Cloud/config';
import classNames from 'clsx';
import React, { type LegacyRef, type ReactElement, forwardRef, memo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { hideOverquotaFeatures } from 'reactApp/appHelpers/featuresHelpers/features/hideOverquotaFeatures';
import { ADS_TIMER_MS } from 'reactApp/constants';
import { useShowAdsBannerTimer } from 'reactApp/hooks/useShowAdsBannerTimer';
import { isCloudOrMailOrApiOverQuota } from 'reactApp/modules/userQuota/overquota.helpers';
import { DisableButton } from 'reactApp/ui/AdvBanners/DisableButton/DisableButton';
import { captureException } from 'reactApp/utils/tracer';

import styles from './SidebarBanner.css';

const DOM_ID_HOME_SIDEBAR_SMALL = config.get('DOM_ID_HOME_SIDEBAR_SMALL');
const DOM_ID_HOME_SIDEBAR = config.get('DOM_ID_HOME_SIDEBAR');

interface IProps {
    isSmall: boolean;
}

export const SidebarBanner = memo(
    forwardRef(({ isSmall }: IProps, ref: LegacyRef<HTMLDivElement>): ReactElement | null => {
        const showAD = !!DOM_ID_HOME_SIDEBAR && !!DOM_ID_HOME_SIDEBAR_SMALL;
        const isCloudOrMailOrApiOverQuotaFlag = useSelector(isCloudOrMailOrApiOverQuota);
        // tempexp_19377-next-line
        const hideDisableButton = hideOverquotaFeatures?.disableAdButton?.mainPage && isCloudOrMailOrApiOverQuotaFlag;

        const updateAds = useCallback(() => {
            if (!showAD) {
                return;
            }

            try {
                if (isSmall) {
                    Rubles?.updateHomeSidebarSmall();
                } else {
                    Rubles?.updateHomeSidebar();
                }
            } catch (error) {
                captureException(error, { issueKey: 'SidebarBanner error' });
            }
        }, [showAD, isSmall]);

        const renderItem = () => {
            if (!showAD) {
                return null;
            }
            return (
                <div
                    className={classNames(styles.root, {
                        [styles.root__small]: isSmall,
                    })}
                    ref={ref}
                    id="sidebar-banner"
                >
                    {!hideDisableButton && (
                        <div className={styles.header}>
                            {isSmall ? (
                                <DisableButton id="sidebar_hm" className={classNames(styles.headerButton, styles.headerButton__wide)} />
                            ) : (
                                <>
                                    <span className={styles.headerText}>Реклама</span>
                                    <DisableButton id="sidebig_hm" text="Отключить" className={styles.headerButton} />
                                </>
                            )}
                        </div>
                    )}
                    <div
                        className={classNames(styles.content, {
                            [styles.content__small]: isSmall,
                        })}
                        id={isSmall ? DOM_ID_HOME_SIDEBAR_SMALL : DOM_ID_HOME_SIDEBAR}
                    />
                </div>
            );
        };

        return useShowAdsBannerTimer({
            adsUpdateTimerMs: ADS_TIMER_MS,
            updateAds,
            suspendTimer: !showAD,
            renderItem,
        });
    })
);

SidebarBanner.displayName = 'SidebarBanner';
